import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import { IoMdClose } from "react-icons/io";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import { calculateStampDuty } from "../../../../utils/helpers/algerianTax";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import { useTranslation } from "react-i18next";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import DefaultButton from "../../../../components/Buttons/Default";
import TaxModal from "../Quotes/Modals/Tax";
import DiscountModal from "../Quotes/Modals/Discount";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";

const calcRowTotal = (row) => {
  const quantity = parseFloat(row.quantity) || 0;
  const unitPrice = parseFloat(row.unitPrice) || 0;
  const taxRate = parseFloat(row.taxRate) || 0;
  const discountRate = parseFloat(row.discountRate) || 0;
  const base = quantity * unitPrice;

  return base - (base * discountRate / 100) + (base * taxRate / 100);
};

function CreateInvoicePage() {
  const { t } = useTranslation();
  const { datePickerFormat, updateSettings } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const urlCustomerId = searchParams.get('customer_id') ? searchParams.get('customer_id'): null;
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [companySettings, setCompanySettings] = useState({});
  const [taxModalOpen, setTaxModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    customer: state?.invoiceData?.customer ? { value: state.invoiceData.customer_id, label: state.invoiceData?.customer?.company_name } : null,
    customer_id: state?.invoiceData?.customer_id || urlCustomerId || "",
    invoice_number: state?.invoiceData?.invoice_number || "",
    invoice_date: state?.invoiceData?.invoice_date ? parseISO(state.invoiceData.invoice_date) : new Date(),
    due_date: state?.invoiceData?.due_date ? parseISO(state.invoiceData.due_date) : new Date(new Date().setDate(new Date().getDate() + 15)),
    terms: state?.invoiceData?.terms || '',
    sales_person_id: state?.invoiceData?.sales_person_id || null,
    tax_rate: state?.invoiceData?.tax_rate || 0,
    discount_rate: state?.invoiceData?.discount_rate || 0,
    stamp_duty_applied: state?.invoiceData?.stamp_duty_applied || false,
    discount_type: state?.invoiceData?.discount_type || 'percent',
    notes: state?.invoiceData?.notes || '',
  });
  const [rows, setRows] = useState(
    state?.invoiceData?.items?.length > 0 ? state.invoiceData.items.map((item, index) => ({
      id: Date.now() + index,
      product_name: item.product_name,
      product_id: item.product_id,
      description: item.description,
      quantity: item.quantity,
      unitPrice: item.unit_price,
      availableQty: 1,
      discountRate: item.discount_rate || 0,
      total: calcRowTotal({
        quantity: item.quantity,
        unitPrice: item.unit_price,
        taxRate: item.tax_rate,
        discountRate: item.discount_rate,
      })
    })) : [{
      id: Date.now(),
      product_name: '',
      product_id: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      availableQty: 1,
      discountRate: 0,
      total: 0
    }]
  );

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/sales/invoices/create');

    if(data.success) {
      setCustomersData(data.customers);
      setEmployeesData(data.employees);
      setCompanySettings(data.company_settings);
      updateSettings(data.company_settings);
      if(invoiceData?.invoice_number === "") {
        setInvoiceData(prev => ({
          ...prev,
          invoice_number: data.invoice_number
        }));
      }
      setInitLoading(false);
    }
  }

  const handleAddRow = () => {
    setRows([...rows, {
      id: Date.now(),
      product_name: '',
      product_id: null,
      description: '',
      quantity: 1,
      unitPrice: 0,
      discountRate: 0,
      total: 0
    }]);
  };

  const handleDeleteRow = (id) => {
    setRows(rows.filter(row => row.id !== id));
  };

  const saveInvoice = async () => {
    setSaveLoading(true);

    const url = id ? '/sales/invoices/update/' + id : '/sales/invoices/store';
    const data = await post(url, {
      ...invoiceData,
      invoice_date: invoiceData.invoice_date ? format(invoiceData.invoice_date, 'yyyy-MM-dd') : null,
      due_date: invoiceData.due_date ? format(invoiceData.due_date, 'yyyy-MM-dd') : null,
      items: rows
    });

    if(data.success) {
      navigate('/sales/invoices');
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  }

  const onTermsChange = (selected) => {
    const value = selected ? selected.value : '';
    let dueDate = new Date(invoiceData.invoice_date);
    if(value === 'net_15') dueDate.setDate(dueDate.getDate() + 15);
    else if(value === 'net_30') dueDate.setDate(dueDate.getDate() + 30);
    else if(value === 'net_60') dueDate.setDate(dueDate.getDate() + 60);
    else if(value === 'net_90') dueDate.setDate(dueDate.getDate() + 90);
    setInvoiceData({ ...invoiceData, terms: value, due_date: dueDate });
  }

  const onInvoiceDateChange = (d) => {
    let dueDate = new Date(d);
    if(invoiceData.terms === 'net_15') dueDate.setDate(dueDate.getDate() + 15);
    else if(invoiceData.terms === 'net_30') dueDate.setDate(dueDate.getDate() + 30);
    else if(invoiceData.terms === 'net_60') dueDate.setDate(dueDate.getDate() + 60);
    else if(invoiceData.terms === 'net_90') dueDate.setDate(dueDate.getDate() + 90);
    setInvoiceData({ ...invoiceData, invoice_date: d, due_date: dueDate });
  }

  const calcPrices = () => {
    const taxRate = invoiceData.tax_rate || 0;
    const discountRate = invoiceData.discount_rate || 0;
    const subTotal = rows.reduce((acc, row) => acc + row.total, 0);
    const discountAmount = function() {
      if(invoiceData.discount_type === 'percent') {
        return subTotal * discountRate / 100;
      } else {
        return discountRate;
      }
    };
    const taxAmount = (subTotal - discountAmount()) * taxRate / 100;
    const amountDue = subTotal - discountAmount() + taxAmount;
    const stampDuty = invoiceData.stamp_duty_applied ? calculateStampDuty(amountDue) : 0;
    const total = amountDue + stampDuty;

    return {
      subTotal,
      discountAmount: discountAmount(),
      taxAmount,
      stampDuty,
      total
    };
  }

  const updateRow = (key, value) => {
    setRows(prev => prev.map(row => {
      if(row.id !== key) return row;
      const updated = { ...row, ...value };
      return { ...updated, total: calcRowTotal(updated) };
    }));
  };

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('sales.invoices'), id ? t('sales.editInvoice') : t('sales.newInvoice')]}
          onBack={() => navigate('/sales/invoices')}
        />
        <div className="subheader-page-content scrollable">
          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.client')}: </Label>
              <CustomSelect
                value={invoiceData.customer || null}
                onChange={selected => setInvoiceData({ ...invoiceData, customer: selected, customer_id: selected ? selected.value : null })}
                module="crm/customers"
                placeholder={t('sales.searchCustomer')}
                createLink="/crm/customers/create"
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.invoiceNumber')}</Label>
              <Input
                type="text"
                value={invoiceData.invoice_number}
                readOnly
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.invoiceDate')}</Label>
              <DatePicker
                selected={invoiceData.invoice_date}
                onChange={onInvoiceDateChange}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>

            <Col md="3" className="mb-3">
              <Label>{t('sales.terms')}</Label>
              <Select
                options={[
                  { value: 'due_on_receipt', label: t('preferences.dueOnReceipt') },
                  { value: 'net_15', label: t('preferences.net15') },
                  { value: 'net_30', label: t('preferences.net30') },
                  { value: 'net_60', label: t('preferences.net60') },
                  { value: 'net_90', label: t('preferences.net90') },
                ]}
                styles={SelectCustomStyles}
                value={invoiceData.terms ? { value: invoiceData.terms, label: invoiceData.terms } : null}
                onChange={onTermsChange}
              />
            </Col>

            <Col md="3" className="mb-3">
              <Label>{t('sales.dueDate')}</Label>
              <DatePicker
                selected={invoiceData.due_date}
                onChange={(d) => setInvoiceData({ ...invoiceData, due_date: d })}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row>
            <Col md="6" className="mb-3">
              <Label>{t('sales.salesperson')}: </Label>
              <Select
                options={employeesData.map(employee => ({ value: employee.id, label: employee.firstname }))}
                styles={SelectCustomStyles}
                value={employeesData.map(e => ({ value: e.id, label: e.firstname})).find(o => o.value === invoiceData.sales_person_id) || null}
                onChange={selected => setInvoiceData({ ...invoiceData, sales_person_id: selected ? selected.value : null })}
              />
            </Col>
          </Row>

          <Table bordered>
            <thead>
              <tr>
                <th>{t('common.item')}</th>
                <th width="100px">{t('common.quantity')}</th>
                <th width="100px">{t('common.unitPrice')}</th>
                <th width="120px">{t('sales.taxPercent')}</th>
                <th width="120px">{t('sales.discountPercent')}</th>
                <th width="140px">{t('common.total')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((data, key) => (
                <tr key={key}>
                  <td style={{minWidth: 200}}>
                    <CustomSelect
                      value={data.product_name ? { value: data.product_id, label: data.product_name } : null}
                      onChange={(selected, objectData) => {
                        setRows(prev => prev.map(r => {
                          if (r.id !== data.id) return r;
                          const unitPrice = objectData?.selling_price ?? r.unitPrice;
                          return {
                            ...r,
                            product_id: selected ? selected.value : null,
                            product_name: selected ? selected.label : '',
                            unitPrice,
                            total: calcRowTotal({
                              quantity: r.quantity,
                              unitPrice,
                              taxRate: r.taxRate,
                              discountRate: r.discountRate,
                            }),
                            availableQty: objectData?.current_stock ?? 1,
                          };
                        }));
                      }}
                      module="inventory/products"
                      placeholder={t('inventory.searchProduct')}
                      createLink="/inventory/products/create"
                      canCreate
                    />
                    <Input
                      type="textarea"
                      rows="1"
                      value={data.description}
                      onChange={e => updateRow(data.id, { description: e.target.value })}
                    />
                    {data.availableQty < data.quantity &&
                      <small className="text-danger d-block">
                        {t('sales.qtyError', { qty: data.availableQty })}
                      </small>
                    }
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.quantity}
                      onChange={e => updateRow(data.id, { quantity: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.unitPrice}
                      onChange={e => updateRow(data.id, { unitPrice: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.taxRate || 0}
                      onChange={e => updateRow(data.id, { taxRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.discountRate || 0}
                      onChange={e => updateRow(data.id, { discountRate: e.target.value })}
                    />
                  </td>
                  <td>
                    <Input
                      type="number"
                      value={data.total}
                      readOnly
                    />
                  </td>
                  <td>
                    <IoMdClose onClick={() => handleDeleteRow(data.id)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Row>
            <Col lg="7" md="6">
              <Row className="mb-3 g-2">
                <Col md="4">              
                  <DefaultButton 
                    color="primary" 
                    outline size="sm" 
                    onClick={handleAddRow}
                    block
                  >
                    + {t('common.addNewRow')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="success"
                    outline
                    size="sm"
                    block
                    onClick={() => setDiscountModalOpen(true)}
                  >
                    + {t('sales.addDiscount')}
                  </DefaultButton>
                </Col>
                <Col md="4">
                  <DefaultButton
                    color="light"
                    outline
                    size="sm"
                    block
                    onClick={() => setTaxModalOpen(true)}
                  >
                    + {t('sales.editTax')}
                  </DefaultButton>
                </Col>
              </Row>

              <FormGroup>
                <Label>{t('sales.notes')}</Label>
                <Input
                  type="textarea"
                  value={invoiceData.notes}
                  onChange={e => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                />
              </FormGroup>
            </Col>

            <Col lg="5" md="6">
              <div className="bg-white p-3">
                <Row className="mb-2">
                  <Col md="6">
                    <strong>{t('common.subTotal')}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().subTotal.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                {invoiceData.discount_rate > 0 &&
                  <Row className="mb-2 text-danger">
                    <Col md="6">
                      <strong>
                        {t('sales.discountWithRate')}: 
                        ({ invoiceData.discount_rate }
                        { invoiceData?.discount_type === 'percent' ? '%' : '' })
                      </strong>
                    </Col>
                    <Col md="6" className="text-end">
                      -{ calcPrices().discountAmount.toFixed(2) } {' '}
                      { companySettings?.sales?.base_currency }
                    </Col>
                  </Row>
                }

                <Row className="mb-3">
                  <Col md="6">
                    <strong>{t('sales.taxWithRate', { rate: invoiceData.tax_rate })}: </strong>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().taxAmount.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                <Row className="mb-3 align-items-center">
                  <Col md="6">
                    <div className="d-flex align-items-center">
                      <Input
                        type="checkbox"
                        className="mt-0"
                        checked={!!invoiceData.stamp_duty_applied}
                        onChange={(e) => setInvoiceData({ ...invoiceData, stamp_duty_applied: e.target.checked })}
                      />
                      <Label className="ms-2 mb-0">
                        <strong>{t('legal.stampDuty')}</strong>
                        <small className="text-muted d-block">{t('legal.stampDutyHint')}</small>
                      </Label>
                    </div>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().stampDuty.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>

                <hr />

                <Row>
                  <Col md="6">
                    <h5 className="fw-bold mb-0">{t('common.total')}: </h5>
                  </Col>
                  <Col md="6" className="text-end">
                    { calcPrices().total.toFixed(2) } {' '}
                    { companySettings?.sales?.base_currency }
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>

      <TaxModal 
        isOpen={taxModalOpen} 
        toggle={() => setTaxModalOpen(false)} 
        taxRate={invoiceData.tax_rate}
        onTaxRateChange={(newTaxRate) => setInvoiceData(prev => ({ ...prev, tax_rate: newTaxRate }))}
      />

      <DiscountModal 
        isOpen={discountModalOpen} 
        toggle={() => setDiscountModalOpen(false)} 
        discountRate={invoiceData.discount_rate}
        onDiscountRateChange={(newDiscountRate) => setInvoiceData(prev => ({ ...prev, discount_rate: newDiscountRate }))}
        discountType={invoiceData.discount_type}
        onDiscountTypeChange={(newDiscountType) => setInvoiceData(prev => ({ ...prev, discount_type: newDiscountType }))}
      />

      <FixedSave
        onSave={saveInvoice}
        loading={saveLoading}
        disabled={saveLoading || !invoiceData?.customer_id}
      />
    </SidebarLayout>
  );
}

export default CreateInvoicePage;
